/**
 * AdministratorsTab Component
 * Manages organization administrators and team members
 */
import "./Batch6Administrators.css";
import { useLayoutEffect, useRef } from "react";
import { Button } from "../../components/ui/button";
import { Plus, Trash2, Clock, Mail } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const ACCESS_LEVELS = [
  { id: "full", label: "Full administrator" },
  { id: "operations", label: "Operations manager" },
  { id: "finance", label: "Finance manager" },
  { id: "view_only", label: "View only" },
];

const text = (value) => typeof value === "string" ? value : "";
const identity = (value) => text(value).trim() ? value : null;

// Presentation only: admin_access.py owns authorization. Missing legacy presets
// default to full there; granular permissions must not be guessed into presets.
const inferAccessLevel = (admin, isOwner) => {
  const level = isOwner ? "full" : (admin.access_level || "full");
  return ACCESS_LEVELS.some(({ id }) => id === level) ? level : "unavailable";
};

const AdministratorsTab = ({ 
  user,
  admins,
  ownerUserId,
  adminUserId,
  setInviteDialogOpen,
  handleResendAdminInvite,
  handleRemoveAdmin,
  handleUpdateAdminAccess,
  isAccessUpdateBlocked = () => false,
  isAccessUpdateUnknown = () => false,
  needsAccessVerification = () => false,
  handleRefreshAdminAccess = () => {},
}) => {
  const rosterRef = useRef(null);
  useLayoutEffect(() => {
    const root = rosterRef.current;
    let panel;
    const measure = () => {
      const rect = panel?.getBoundingClientRect();
      const visible = rect && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight &&
        getComputedStyle(panel).visibility !== "hidden";
      // Reserve scroll travel only for the stacked mobile roster. Include the
      // panel's bottom inset, not an assumed consent height or fixture value.
      const clearance = window.innerWidth <= 760 && visible
        ? Math.max(0, window.innerHeight - Math.max(0, rect.top)) + 8 : 0;
      root.style.setProperty("--admin-consent-clearance", `${clearance}px`);
    };
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    const panelChanges = new MutationObserver(measure);
    const connect = () => {
      const next = document.getElementById("sporthouse-analytics-consent");
      if (next !== panel) {
        resize?.disconnect();
        panelChanges.disconnect();
        panel = next;
        if (panel) {
          resize?.observe(panel);
          panelChanges.observe(panel, { attributes: true, childList: true, subtree: true });
        }
      }
      measure();
    };
    // The shared consent UI mounts independently; observe insertion/removal,
    // never mutate it or subscribe to unrelated body attribute changes.
    const mounts = new MutationObserver(connect);
    mounts.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    connect();
    return () => {
      mounts.disconnect(); panelChanges.disconnect(); resize?.disconnect();
      window.removeEventListener("resize", measure);
      root.style.removeProperty("--admin-consent-clearance");
    };
  }, []);
  const ownerIds = new Set([ownerUserId, adminUserId].filter(identity));
  const activeIdentity = (admin) => identity(admin.user_id) && (admin.status == null || admin.status === "active");
  // Collapse only repeated records with every stored field equal. Distinct
  // admin IDs, presets, permissions or other metadata must remain observable.
  const sameRecord = (left, right) => Object.keys(left).length === Object.keys(right).length &&
    Object.keys(left).every((key) => Object.prototype.hasOwnProperty.call(right, key) && (
      Object.is(left[key], right[key]) ||
      (Array.isArray(left[key]) && Array.isArray(right[key]) && left[key].length === right[key].length &&
        left[key].every((value, index) => Object.is(value, right[key][index])))
    ));
  const rows = (Array.isArray(admins) ? admins : []).filter((admin, index, records) => {
    if (!admin || typeof admin !== "object" || Array.isArray(admin)) return false;
    if (!identity(admin.user_id) && !identity(admin.email)) return false;
    return !activeIdentity(admin) || !records.slice(0, index).some((previous) =>
      previous && activeIdentity(previous) && previous.user_id === admin.user_id && sameRecord(previous, admin));
  });
  const conflictingIds = new Set(rows.filter((admin, index) => activeIdentity(admin) &&
    rows.some((other, otherIndex) => otherIndex !== index && activeIdentity(other) && other.user_id === admin.user_id)
  ).map((admin) => admin.user_id));
  ownerIds.forEach((userId) => {
    if (!rows.some((admin) => admin.user_id === userId)) {
      rows.unshift({ user_id: userId, ...(user?.user_id === userId ? { name: user.name, email: user.email } : {}) });
    }
  });
  return (
    <div ref={rosterRef} className="sh-org-administrator-settings-batch6" data-testid="administrators-tab">
      <div className="admin-roster">
        <div className="admin-roster-header">
          <div>
            <h3 className="admin-roster-title">Team Members</h3>
            <p className="text-sm text-slate-500">Manage who has access to your organization</p>
          </div>
          <Button
            onClick={() => setInviteDialogOpen(true)}
            className="sh-action admin-invite"
            data-testid="invite-admin-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Invite Admin
          </Button>
        </div>

        <div className="admin-roster-list" role="list" aria-label="Organization administrators">
          {rows.map((admin, index) => {
            const isOwner = ownerIds.has(admin.user_id);
            const isConflict = conflictingIds.has(admin.user_id);
            const name = text(admin.name) || text(admin.email) || admin.user_id;
            const unknown = isAccessUpdateUnknown(admin.admin_id);
            const verification = needsAccessVerification(admin.admin_id);
            const level = (unknown || verification) && !isOwner ? "unavailable" : inferAccessLevel(admin, isOwner);
            return (
            <div key={`${identity(admin.user_id) || 'invite'}-${index}`} className="admin-roster-row" role="listitem" data-testid={`admin-row-${text(admin.email)}`}>
              <div className="admin-identity">
                <div className="admin-avatar" aria-hidden="true">
                  {name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{name}</p>
                  {isOwner && <p className="admin-owner">Organization owner</p>}
                  <p className="text-sm text-slate-500">{text(admin.email)}</p>
                  {isConflict && <p className="text-xs text-amber-700">Membership conflict: multiple stored records. Reconcile before changing access.</p>}
                  {unknown && <p className="text-xs text-amber-700">Access update outcome unknown. Do not retry. Reload settings to verify stored access.</p>}
                  {verification && <p className="text-xs text-amber-700">Access update completed in another settings lifetime. Verify stored access before editing.</p>}
                  {verification && !isOwner && !isConflict && <Button variant="outline" aria-label={`Refresh access for ${name}`} onClick={() => handleRefreshAdminAccess(admin.admin_id)}>Refresh access</Button>}
                  {!unknown && !verification && Array.isArray(admin.permissions) && admin.permissions.length > 0 && (
                    <p className="text-xs text-slate-500">Stored permissions: {admin.permissions.filter((permission) => typeof permission === "string").join(", ")}</p>
                  )}
                </div>
              </div>
              <div className="admin-controls">
                <Select
                  value={level}
                  disabled={isOwner || isConflict || level === "unavailable" || isAccessUpdateBlocked(admin.admin_id)}
                  onValueChange={(value) => { if (!isOwner && !isConflict && !isAccessUpdateBlocked(admin.admin_id)) handleUpdateAdminAccess(admin, value); }}
                >
                  <SelectTrigger className="admin-access" aria-label={`Access level for ${name}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {level === "unavailable" && <SelectItem value="unavailable">Access unavailable</SelectItem>}
                    {ACCESS_LEVELS.map((level) => <SelectItem key={level.id} value={level.id}>{level.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {admin.status === "pending" && !isOwner && !isConflict ? (
                  <>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Invite Pending
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full text-xs"
                      onClick={() => handleResendAdminInvite(admin)}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Resend
                    </Button>
                  </>
                ) : (
                  <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
                    {admin.status === "active" ? "Active" : admin.status == null ? "Status not recorded" : text(admin.status) || "Status unavailable"}
                  </span>
                )}
                {!isOwner && !isConflict && <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleRemoveAdmin(admin.user_id || admin.email)}
                  data-testid={`remove-admin-${admin.email}`}
                  aria-label={`Remove ${name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>}
              </div>
            </div>
          );})}

          {rows.length === 0 && (
            <p className="text-center text-slate-500 py-8">
              No additional administrators. Invite team members to help manage your organization.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdministratorsTab;
